// Advanced Filters Model - Property search filters
import {proxy, ref} from "valtio";

import type {Amenity, HousingType} from "@/entities/rental/api/types/search.types";
import {AdvanceFilter} from "@/entities/rental/types";

import {IQueryParamProvider} from "@/shared/contracts";
import {EventEmitter} from "@/shared/event-emitter";
import {Checkbox, type CheckboxInstance, Range, type RangeInstance} from "@/shared/models/form/fields";

interface AdvancedFiltersEvents {
    apply: () => void;
}

// Property type item with checkbox instance
export interface PropertyTypeItem {
    type: string;
    displayName: string;
    count: number;
    checkbox: CheckboxInstance;
}

// Amenity item with checkbox instance
export interface AmenityItem {
    id: string;
    name: string;
    displayName: string;
    count: number;
    checkbox: CheckboxInstance;
}

// Helper type for generic list sync
interface SyncableItem {
    checkbox: CheckboxInstance;
}

/**
 * Generic helper to sync a list with server data while preserving selections
 */
function syncListWithSelection<TItem extends SyncableItem, TServerItem>(
    currentItems: Array<TItem>,
    serverItems: Array<TServerItem>,
    getKeys: (item: TItem) => Array<string>,
    getServerKeys: (item: TServerItem) => Array<string>,
    mapServerItem: (item: TServerItem, isSelected: boolean) => TItem,
    mapSelectedItem: (item: TItem) => TItem,
): Array<TItem> {
    // Get selected items and their keys
    const selectedItems = currentItems.filter((item) => item.checkbox.checked);
    const selectedKeys = new Set(selectedItems.flatMap(getKeys));

    // Build new list from server response
    const newItems = serverItems.map((serverItem) => {
        const keys = getServerKeys(serverItem);
        const isSelected = keys.some((key) => selectedKeys.has(key));
        return mapServerItem(serverItem, isSelected);
    });

    // Add selected items not in server response
    const newItemKeys = new Set(serverItems.flatMap(getServerKeys));
    selectedItems.forEach((selected) => {
        const keys = getKeys(selected);
        const isInNewList = keys.some((key) => newItemKeys.has(key));
        if (!isInNewList) {
            newItems.push(mapSelectedItem(selected));
        }
    });

    return newItems;
}

class AdvancedFiltersClass implements IQueryParamProvider {
    readonly events: EventEmitter<AdvancedFiltersEvents>;

    // Range fields
    bedrooms: RangeInstance;
    bathrooms: RangeInstance;
    nightlyRate: RangeInstance;

    // Property types (dynamic from backend)
    private _propertyTypes: Array<PropertyTypeItem> = [];

    // Amenities (dynamic from backend)
    private _amenities: Array<AmenityItem> = [];

    constructor(config: AdvanceFilter) {
        this.events = ref(new EventEmitter<AdvancedFiltersEvents>()) as EventEmitter<AdvancedFiltersEvents>;
        // Initialize ranges
        this.bedrooms = Range({
            minValue: null,
            maxValue: null,
            minLabel: "Minimum bedrooms",
            maxLabel: "Maximum bedrooms",
        });

        this.bathrooms = Range({
            minValue: null,
            maxValue: null,
            minLabel: "Minimum bathrooms",
            maxLabel: "Maximum bathrooms",
        });

        this.nightlyRate = Range({
            minValue: null,
            maxValue: null,
            minLabel: "Minimum nightly rate",
            maxLabel: "Maximum nightly rate",
        });

        // Initialize property types from config
        this.setPropertyTypes(config.housingTypes);

        // Initialize amenities from config
        this.setAmenities(config.amenities);
    }

    // Get property types list for rendering
    get propertyTypes(): Array<PropertyTypeItem> {
        return this._propertyTypes;
    }

    // Set property types from backend data
    setPropertyTypes(housingTypes: Array<HousingType>): void {
        this._propertyTypes = syncListWithSelection(
            this._propertyTypes,
            housingTypes,
            (item) => [item.type.toLowerCase()],
            (serverItem) => [serverItem.type.toLowerCase()],
            (serverItem, isSelected) => ({
                type: serverItem.type,
                displayName: serverItem.displayName,
                count: serverItem.count,
                checkbox: Checkbox({initialValue: isSelected}),
            }),
            (selected) => ({
                type: selected.type,
                displayName: selected.displayName,
                count: 0,
                checkbox: Checkbox({initialValue: true}),
            }),
        );
    }

    // Get amenities list for rendering
    get amenities(): Array<AmenityItem> {
        return this._amenities;
    }

    // Set amenities from backend data
    setAmenities(amenities: Array<Amenity>): void {
        this._amenities = syncListWithSelection(
            this._amenities,
            amenities,
            (item) => [item.id.toLowerCase(), item.name.toLowerCase()],
            (serverItem) => [serverItem.id.toLowerCase(), serverItem.name.toLowerCase()],
            (serverItem, isSelected) => ({
                id: serverItem.id,
                name: serverItem.name,
                displayName: serverItem.displayName,
                count: serverItem.count,
                checkbox: Checkbox({initialValue: isSelected}),
            }),
            (selected) => ({
                id: selected.id,
                name: selected.name,
                displayName: selected.displayName,
                count: 0,
                checkbox: Checkbox({initialValue: true}),
            }),
        );
    }

    // Get selected property types
    get selectedPropertyTypes(): Array<string> {
        return this._propertyTypes.filter((item) => item.checkbox.checked).map((item) => item.type);
    }

    // Get selected amenities
    get selectedAmenities(): Array<string> {
        return this._amenities.filter((item) => item.checkbox.checked).map((item) => item.name);
    }

    // Count active filters
    get activeFiltersCount(): number {
        let count = 0;
        if (this.bedrooms.hasValue) count++;
        if (this.bathrooms.hasValue) count++;
        if (this.nightlyRate.hasValue) count++;
        count += this.selectedPropertyTypes.length;
        count += this.selectedAmenities.length;
        return count;
    }

    // Check if any filter is active
    get hasActiveFilters(): boolean {
        return this.activeFiltersCount > 0;
    }

    // Alias for hasActiveFilters
    get hasValue(): boolean {
        return this.hasActiveFilters;
    }

    sync(config: AdvanceFilter): void {
        this.setPropertyTypes(config.housingTypes);
        this.setAmenities(config.amenities);

        // Validate range fields after config update
        this.validate();
    }
    // Clear all filters
    clear(): void {
        this.bedrooms.clear();
        this.bathrooms.clear();
        this.nightlyRate.clear();

        this._propertyTypes.forEach((item) => item.checkbox.clear());
        this._amenities.forEach((item) => item.checkbox.clear());
    }

    // Get query params (IQueryParamProvider implementation)
    // Format: minBeds=1&maxBeds=2&housingType=house,condo&amenities=hottub,pool
    getQueryParam(): string {
        const params: Array<string> = [];

        // Bedrooms - format: minBeds=1&maxBeds=2
        const bedroomsParam = this.bedrooms.getQueryParam("Beds", {
            minPrefix: "min",
            maxPrefix: "max",
        });
        if (bedroomsParam) params.push(bedroomsParam);

        // Bathrooms - format: minBaths=1&maxBaths=2
        const bathroomsParam = this.bathrooms.getQueryParam("Baths", {
            minPrefix: "min",
            maxPrefix: "max",
        });
        if (bathroomsParam) params.push(bathroomsParam);

        // Nightly rate - format: minRate=100&maxRate=500
        const nightlyRateParam = this.nightlyRate.getQueryParam("Rate", {
            minPrefix: "min",
            maxPrefix: "max",
        });
        if (nightlyRateParam) params.push(nightlyRateParam);

        // Property types - format: housingType=house,condo (comma-separated)
        const selectedTypes = this.selectedPropertyTypes;
        if (selectedTypes.length > 0) {
            params.push(`housingType=${selectedTypes.join(",")}`);
        }

        // Amenities - format: amenities=hottub,pool (comma-separated)
        const selectedAmenities = this.selectedAmenities;
        if (selectedAmenities.length > 0) {
            params.push(`amenities=${selectedAmenities.join(",")}`);
        }

        return params.join("&");
    }

    // Set from query params
    setFromQueryParams(params: URLSearchParams): void {
        // Clear existing values first
        this.clear();

        // Range filters
        this.bedrooms.setFromQueryParams(params, "Beds", {minPrefix: "min", maxPrefix: "max"});
        this.bathrooms.setFromQueryParams(params, "Baths", {minPrefix: "min", maxPrefix: "max"});
        this.nightlyRate.setFromQueryParams(params, "Rate", {minPrefix: "min", maxPrefix: "max"});

        // Property types - format: housingType=house,condo
        this.setCheckboxesFromParam(
            params.get("housingType"),
            this._propertyTypes,
            (item) => [item.type.toLowerCase()],
            (value) => ({
                type: value,
                displayName: value,
                count: 0,
                checkbox: Checkbox({initialValue: true}),
            }),
        );

        // Amenities - format: amenities=hottub,pool
        this.setCheckboxesFromParam(
            params.get("amenities"),
            this._amenities,
            (item) => [item.id.toLowerCase(), item.name.toLowerCase()],
            (value) => ({
                id: value,
                name: value,
                displayName: value,
                count: 0,
                checkbox: Checkbox({initialValue: true}),
            }),
        );

        this.validate();
    }

    /**
     * Helper to set checkboxes from comma-separated URL param
     */
    public setCheckboxesFromParam<T extends SyncableItem>(
        paramValue: string | null,
        items: Array<T>,
        getKeys: (item: T) => Array<string>,
        createItem: (value: string) => T,
    ): void {
        if (!paramValue) return;

        const values = paramValue.split(",").filter(Boolean);
        values.forEach((value) => {
            const lowerValue = value.toLowerCase();
            const existingItem = items.find((item) => getKeys(item).includes(lowerValue));

            if (existingItem) {
                existingItem.checkbox.setChecked(true);
            } else {
                items.push(createItem(value));
            }
        });
    }

    updateFilter(): void {
        this.events.emit("apply");
    }
    private validate(): void {
        this.bedrooms.validate();
        this.bathrooms.validate();
        this.nightlyRate.validate();
    }
}

// Export type
export type AdvancedFiltersInstance = AdvancedFiltersClass;

// Factory function
export function AdvancedFilters(config: AdvanceFilter): AdvancedFiltersInstance {
    return proxy<AdvancedFiltersInstance>(new AdvancedFiltersClass(config));
}
